package com.eladfinish.bible.contest.screens.main.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.material3.Card
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.CustomAccessibilityAction
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.customActions
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.common.ReorderHapticFeedbackType
import com.eladfinish.bible.contest.common.rememberReorderHapticFeedback
import com.eladfinish.bible.contest.screens.pairMatching.model.SortableData
import com.eladfinish.bible.contest.screens.pairMatching.model.UniqueIdentifiable
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.LocalNavigationBarInset
import sh.calvin.reorderable.ReorderableItem
import sh.calvin.reorderable.rememberReorderableLazyGridState

@Composable
fun <T : UniqueIdentifiable> VerticalReorderGrid(
    data: List<T>,
    count: Int = 2,
    itemContent: @Composable BoxScope.(item: T) -> Unit,
    doneButton: @Composable RowScope.() -> Unit,
    resetButton: @Composable RowScope.() -> Unit,
    onItemClick: () -> Unit = {},
    onListChanged: (List<T>) -> Unit = {},
) {
    val haptic = rememberReorderHapticFeedback()
    val lazyGridState = rememberLazyGridState()

    // Saved as ids: the items themselves cannot go into the saved state.
    var order by rememberSaveable { mutableStateOf(data.map { it.id }) }
    val itemsById = remember(data) { data.associateBy { it.id } }
    val list = order.mapNotNull { itemsById[it] }
    // After a rotation the caller starts from the original order again: tell it the restored one.
    LaunchedEffect(Unit) {
        if (order != data.map { it.id }) onListChanged(list)
    }
    val reorderableLazyGridState = rememberReorderableLazyGridState(lazyGridState) { from, to ->
        order = order.toMutableList().apply {
            this[to.index] = this[from.index].also {
                this[from.index] = this[to.index]
            }
        }
        onListChanged(order.mapNotNull { itemsById[it] })
        haptic.performHapticFeedback(ReorderHapticFeedbackType.MOVE)
    }

    val bottomInsetDp = LocalNavigationBarInset.current

    Column {
        Row(
            modifier = Modifier
                .fillMaxWidth()
        ) {
            doneButton()
            resetButton()
        }
        LazyVerticalGrid(
            columns = GridCells.Fixed(count = count),
            modifier = Modifier
                .fillMaxSize()
                .mouseVerticalScrollbar(lazyGridState),
            state = lazyGridState,
            contentPadding = PaddingValues(
                start = 8.dp,
                end = 8.dp,
                top = 8.dp,
                bottom = 8.dp + bottomInsetDp,
            ),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            itemsIndexed(items = list, key = { _, item -> item.id }) { index, item: T ->
                ReorderableItem(state = reorderableLazyGridState, key = item.id) {
                    val interactionSource = remember { MutableInteractionSource() }
                    Card(
                        onClick = {},
                        modifier = Modifier
                            .semantics {
                                customActions = listOf(
                                    CustomAccessibilityAction(
                                        label = "Move Before",
                                        action = {
                                            if (index > 0) {
                                                order = order.toMutableList().apply {
                                                    add(index - 1, removeAt(index))
                                                }
                                                onListChanged(order.mapNotNull { itemsById[it] })
                                                true
                                            } else {
                                                false
                                            }
                                        }
                                    ),
                                    CustomAccessibilityAction(
                                        label = "Move After",
                                        action = {
                                            if (index < list.size - 1) {
                                                order = order.toMutableList().apply {
                                                    add(index + 1, removeAt(index))
                                                }
                                                onListChanged(order.mapNotNull { itemsById[it] })
                                                true
                                            } else {
                                                false
                                            }
                                        }
                                    ),
                                )
                            },
                        interactionSource = interactionSource,
                    ) {
                        Box(
                            modifier = Modifier
                                .draggableHandle(
                                    onDragStarted = {
                                        haptic.performHapticFeedback(ReorderHapticFeedbackType.START)
                                    },
                                    onDragStopped = {
                                        haptic.performHapticFeedback(ReorderHapticFeedbackType.END)
                                    },
                                    interactionSource = interactionSource,
                                )
                                .clearAndSetSemantics { }
                                .clickable {
                                    onItemClick()
                                },
                        ) {
                            itemContent(item)
                        }
                    }
                }
            }
        }
    }
}

@Preview
@Composable
private fun VerticalReorderGridNewPreview() {
    val items: List<SortableData<String>> = List(10) { SortableData(id = it, value = "Item ${it.plus(1)}") }

    VerticalReorderGrid(
        data = items,
        count = 2,
        itemContent = {
            Text(
                text = it.value,
                modifier = Modifier
                    .align(Alignment.Center)
                    .padding(horizontal = 8.dp),
                textAlign = TextAlign.Center,
            )
        },
        doneButton = {
            Text(
                modifier = Modifier
                    .padding(10.dp)
                    .weight(1f)
                    .background(Color.White),
                text = "Done",
            )
        },
        resetButton = {
            Text(
                modifier = Modifier
                    .padding(10.dp)
                    .weight(1f)
                    .background(Color.White),
                text = "Reset",
            )
        },
    )
}
